import _ from 'lodash'
import { isObject, isInteger } from './isType'
import array from './array'


type Key = string | number
type Path = Key[]

/**
 * Converts string path to array path with integer indexes
 * e.g. 'small.step.4.men' → [ 'small', 'step', 4, 'men' ]
 */
const stringToPath = (string: string): Path => (
  string.split('.').map((idx) => {
    const number  = parseInt(idx, 10)
    const isIndex = !Number.isNaN(number)

    if (isIndex) {
      return number
    }

    return idx
  })
)

/**
 * Checks if object has property
 * e.g. 'foo', { bar: 'baz' }, bar → true
 */
const has = (key: Key, object: object): boolean =>
  Object.prototype.hasOwnProperty.call(object, key)

/**
 * Returns difference between passed objects
 * e.g. { foo: 1, bar: 2, zoo: 3 }, { foo: 4, bar: 2, baz: 5 } → { foo: 1, zoo: 3 }
 */
const diff = (left: object, right: object): object => {
  const changes = (left: object, right: object): object => (
    _.transform(left, (result: object, value: any, key: Key) => {
      if (!_.isEqual(value, right[key])) {
        result[key] = (isObject(value) && isObject(right[key])) ? changes(value, right[key]) : value
      }
    })
  )

  return changes(left, right)
}

/**
 * Makes a shallow clone of an object, setting or overriding the specified property with the given value
 * e.g { foo: 'bar' }, 'foo', 'baz' → { foo: 'baz' }
 */
const set = (object: object, key: Key, value: any): object => ({ ...object, [key]: value })

/**
 * Makes a shallow clone of an object, setting or overriding the nodes required to create the given path,
 * and placing the specific value at the tail end of that path
 * e.g { foo: { bar: 'baz' } }, 'foo.bar', 'qux' → { foo: { bar: 'qux' } }
 */
const setIn = (object: object, path: string | Path, value: any): object => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return value
  }

  const idx = path[0]
  let nextValue: any = value

  if (path.length > 1) {
    let nextObject: object

    if (object !== null && has(idx, object)) {
      nextObject = object[idx]
    }
    else if (isInteger(idx)) {
      nextObject = []
    }
    else {
      nextObject = {}
    }

    nextValue = setIn(nextObject, path.slice(1), value)
  }

  if (isInteger(idx) && Array.isArray(object)) {
    const arr = [ ...object ]

    arr[idx] = nextValue

    return arr
  }

  return { ...object, [idx]: nextValue }
}

interface UpdateCallback {
  (value: any): any
}

/**
 * Makes a shallow clone of an object, setting or overriding the specified property by calling function
 * e.g. { foo: 1, bar: 2 }, 'foo', (v) => ++v → { foo: 2, bar: 2 }
 */
const update = (object: object, key: Key, fn: UpdateCallback): object => updateIn(object, [ key ], fn)

/**
 * Makes a shallow clone of an object, setting or overriding the nodes required to create the given path,
 * and placing the specific value returned by function at the tail end of that path
 * e.g. { foo: { bar: 1 }, zoo: 2 }, 'foo.bar', (v) => ++v → { foo: { bar: 2 }, zoo: 2 }
 */
const updateIn = (value: object, path: string | Path, fn: UpdateCallback): object => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return fn(value)
  }

  const idx = path[0]
  let nextValue: any

  if (path.length > 1) {
    let nextObject: object

    if (value !== null && has(idx, value)) {
      nextObject = value[idx]
    }
    else if (isInteger(idx)) {
      nextObject = []
    }
    else {
      nextObject = {}
    }

    nextValue = updateIn(nextObject, path.slice(1), fn)
  }
  else {
    nextValue = fn(value[idx])
  }

  if (isInteger(idx) && Array.isArray(value)) {
    const arr = [ ...value ]

    arr[idx] = nextValue

    return arr
  }

  return { ...value, [idx]: nextValue }
}

/**
 * Remove key / index from object / array
 * e.g. { foo: 1, bar: 2 }, 'foo' → { bar: 2 }
 */
const remove = (value: object | any[], key: Key): object | any[] => {
  if (isInteger(key) && Array.isArray(value)) {
    const result = [ ...value ]
    const index  = typeof key === 'number' ? key : parseInt(key, 10)
    result.splice(index, 1)
    return result
  }

  const res = { ...value }
  delete res[key]
  return res
}

/**
 * Remove key / index in object / array by path
 * e.g. { foo: { bar: 1, baz: 2 } }, 'foo.bar' → { foo: { baz: 2 } }
 */
const removeIn = (value: object | any[], path: string | Path): object | any[] => {
  if (typeof path === 'string') {
    path = stringToPath(path)
  }

  if (path.length === 0) {
    return value
  }

  if (path.length === 1) {
    return remove(value, path[0])
  }

  const head = path[0]
  const tail = path.slice(1)

  // TODO weird...
  if (value[head] == null) {
    return value
  }

  return set(value, head, removeIn(value[head], tail))
}

interface KeyCallback {
  (key: Key): Key
}

/**
 * Map initial object keys with new one
 * e.g. { foo: 1, bar: 2 }, (key) => `sb_${key}` → { sb_foo: 1, sb_bar: 2 }
 */
const mapKeys = (object, callback: KeyCallback): object => {
  if (!isObject(object)) {
    console.warn(`helpers mapValues - ${object} is not object`)
    return object
  }

  const result = {}

  Object.keys(object).forEach((key) => {
    const newKey = callback(key)
    result[newKey] = object[key]
  })

  return result
}

interface ValueKeyCallback {
  (value: any, key: Key): any
}

/**
 * Map initial object values with new one
 * e.g. { foo: 1, bar: 2 }, (key) => `sb_${key}` → { sb_foo: 1, sb_bar: 2 }
 */
const mapValues = <T extends { [key: string]: any }, U extends { [key: string]: any }>(object: T, callback: ValueKeyCallback): T | U => {
  if (!isObject(object)) {
    console.warn(`helpers mapValues - ${object} is not object`)
    return object
  }

  const result: any = {}

  Object.keys(object).forEach((key) => {
    result[key] = callback(object[key], key)
  })

  return result
}

/**
 * Remove keys by passed keys from object
 * e.g. { foo: 1, bar: 2 }, (key) => `sb_${key}` → { sb_foo: 1, sb_bar: 2 }
 */
const removeKeys = (object: object, keys: Key[]): object =>
  filterByKey(object, (key: Key) => !keys.includes(key))

/**
 * Shallow merges of two objects
 * e.g. { name: 'Freddy', age: 10 }, { name: 'Freddy', died: true } → { name: 'Freddy', age: 10, died: true }
 */
const merge = (target: object, source: object): object => ({ ...target, ...source })

/**
 * Deep merge of two objects
 *
 * If a key exists in both objects:
 * - and both values are objects, the two values will be recursively merged
 * - otherwise the value from the second object will be used
 *
 * e.g. { name: 'Fred', health: { age: 10 } }, { name: 'Freddy', health: { died: true } } → { name: 'Freddy', helath: { age: 10, died: true } }
 */
const mergeDeep = (target: object, source: object): object => {
  if (isObject(target) && isObject(source)) {
    const copy: object = {}

    Object.keys(target).forEach((key) => {
      if (typeof source[key] !== 'undefined') {
        copy[key] = mergeDeep(target[key], source[key])
      }
      else {
        copy[key] = target[key]
      }
    })

    Object.keys(source).forEach((key) => {
      if (!has(key, copy)) {
        copy[key] = source[key]
      }
    })

    return copy
  }

  return source
}

/**
 * Deep merge of two objects, dropping keys not existed in target object
 * e.g. { name: 'Fred', health: { age: 10 } }, { name: 'Freddy', health: { sick: true, age: 20 } } → { name: 'Freddy', helath: { age: 20 } }
 */
const defaultsDeep = (target: object, source: object): object => {
  if (typeof source === 'undefined') {
    return target
  }

  if (Array.isArray(target)) {
    return source
  }

  if (target instanceof Object && source instanceof Object) {
    if (!Object.keys(target).length) {
      return { ...source }
    }

    const copy = {}

    Object.keys(target).forEach((key) => {
      copy[key] = defaultsDeep(target[key], source[key])
    })

    return copy
  }

  if (source instanceof Object) {
    return { ...source }
  }

  return source
}

interface FilterByKeyCallback {
  (key: Key): boolean
}

/**
 * Filter object by key
 * e.g. { foo: 1, bar: 2, zoo: 3 }, (key) => [ 'foo', 'bar' ].includes(key) → { foo: 1, bar: 2 }
 */
const filterByKey = (object: object, filter: FilterByKeyCallback): object =>
  Object.keys(object).reduce((result, key) => {
    if (filter(key)) {
      result[key] = object[key]
    }

    return result
  }, {})


interface FilterByValue {
  (value: any): boolean
}

/**
 * Filter object by value
 * { foo: 1, bar: 2, zoo: 3 }, (value) => value > 1 → { bar: 2, zoo: 3 }
 */
const filterByValue = <T extends { [key: string]: any }>(object: T, filter: FilterByValue): T | Partial<T> =>
  Object.keys(object).reduce((result, key) => {
    const value = object[key]

    if (filter(value)) {
      result[key] = value
    }

    return result
  }, {})

/**
 * Get filtered object by keys
 * e.g. { foo: 1, bar: 2, zoo: 3 }, [ 'foo', 'bar' ] → { foo: 1, bar: 2 }
 */
const filterByKeyList = (object: object, keys: Key[]): object =>
  filterByKey(object, (key) => keys.includes(key))

/**
 * Stringify object without removing undefined values
 * By default JSON.stringify() removes undefined values: JSON.stringify({ foo: undefined }) → {}
 *
 * object.stringify({ foo: undefined }) → {}
 */
const stringify = (object: object): string =>
  JSON.stringify(object, (_key, value) => value === undefined ? null : value)

/**
 * Get object param
 * example const fooObject = { foo: { bar: { value: 1 } } } →  const value = get(fooObject, 'foo.bar.value') === 1
 */
const get = (object: object, path: string, defaultValue?: any) => {
  if (
    object === null
    || object === undefined
    || Array.isArray(object)
    || typeof object !== 'object'
  ) {
    console.warn('Type off object param is not Object type ', object)

    if (defaultValue) {
      return defaultValue
    }

    return null
  }

  if (typeof path !== 'string') {
    console.warn('Type off path param is not String type ', object)
    return null
  }

  const pathArrValue = path.split('.')
  let result: object = typeof window !== 'undefined' && object === window ? window : { ...object }
  let index: number = 0

  while (result !== null && index < pathArrValue.length) {
    const value = result[pathArrValue[index]]

    if (!value) {
      result = null
    }
    else {
      result = value
      index += 1
    }
  }

  if (!result) {
    return defaultValue
  }

  return result
}

/**
 * Merge object without override values
 * example const object1 = { device: 'mobile', type: 'iphone'}, const object2 = { device: 'desktop', type: 'macbook'}
 * result = joinStringValues(object1, object2) -> { device: 'mobile,desktop', type: 'iphone,macbook'}
 * @param args
 */
const joinStringValues = (...args: object[]): object => {
  const result = {}

  if (args.length) {
    args.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (!Object.keys(result).includes(key)) {
          result[key] = item[key]
        }
        else {
          const values = result[key].split(',')

          if (!values.includes(item[key])) {
            values.push(item[key])

            result[key] = values.join(',')
          }
        }
      })
    })
  }

  return result
}

/**
 * Toggle object value
 * example const object1 = { device: 'mobile,mobile2', type: 'iphone'}, const object2 = { device: 'mobile2'}
 * result = toggleValue(object1, object2) -> { device: 'mobile', type: 'iphone'}
 * @param defaultParams {Object}
 * @param params {Object}
 */
const toggleValue = (defaultParams: object, params: object): object => {
  const temp = { ...defaultParams }

  Object.keys(params).forEach((key) => {
    if (temp[key]) {
      let values = temp[key].split(',')

      values = array.toggle(values, params[key])

      if (values.length) {
        temp[key] = values.join(',')
      }
      else {
        delete temp[key]
      }
    }
    else {
      temp[key] = params[key]
    }
  })

  return temp
}


export default {
  isObject,
  stringToPath,
  has,
  diff,

  set,
  setIn,
  update,
  updateIn,
  remove,
  removeIn,

  mapKeys,
  mapValues,
  removeKeys,

  merge,
  mergeDeep,
  defaultsDeep,

  filterByKey,
  filterByValue,
  filterByKeyList,

  stringify,
  get,

  joinStringValues,
  toggleValue,
}
